import useOutsideClick from "@/app/hooks/useOutsideClick";
import { setOpenMenu } from "@/store/drawerMenuOpenSlice";
import { useAppDispatch } from "@/store/hooks";
import type { OverlayDrawerLayoutVariant, OverlayMenuType } from "@/types/overlayMenu";
import { useWindowWidth } from "@react-hook/window-size";
import CaretLeftIcon from "public/next-public/common/icons/caret-solid-left.svg";
import CloseIcon from "public/next-public/common/icons/close-icon.svg";
import { useEffect, useId, useRef, useState } from "react";
import style from "./drawerMenu.module.scss";

const CLOSE_ANIMATION_MS = 300;
const BOOTSTRAP_SM_BREAKPOINT = 576;

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const DEFAULT_CLOSE_OPTIONS: Required<DrawerMenuCloseOptions> = {
  closeRequested: false,
  menuTriggerIgnoreSelectors: [],
};

export default function DrawerMenu({
  showMenu,
  showBackArrow = false,
  headerText,
  children,
  id,
  onClose,
  onBack,
  mobileMenuClass: mobileMenuClassname,
  preserveLowerNav = false,
  switchState = null,
  closeOptions,
  layoutVariant = "belowHeader",
  headerLine = false,
}: DrawerMenuProps) {
  const { closeRequested, menuTriggerIgnoreSelectors } = { ...DEFAULT_CLOSE_OPTIONS, ...closeOptions };
  const { switchingToMenu = null, onSwitchComplete, menuKey } = switchState ?? {};
  const menuRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const dispatch = useAppDispatch();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < BOOTSTRAP_SM_BREAKPOINT;
  const [isClosing, setIsClosing] = useState(false);
  const [useFastClose, setUseFastClose] = useState(false);
  /** True after a switch-close completes so the next open uses fast (0.15s) animation; cleared when menu closes. */
  const [useFastOpen, setUseFastOpen] = useState(false);

  const handleCloseMenu = () => {
    if (isClosing) return;
    setIsClosing(true);
    setUseFastClose(false);
    setTimeout(() => {
      setIsClosing(false);
      dispatch(setOpenMenu("none"));
      onClose();
    }, CLOSE_ANIMATION_MS);
  };

  const handleCloseMenuRef = useRef(handleCloseMenu);
  handleCloseMenuRef.current = handleCloseMenu;

  const isVisible = showMenu || isClosing;

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseMenuRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isVisible]);

  useOutsideClick(menuRef, handleCloseMenu, isVisible, menuTriggerIgnoreSelectors);

  useEffect(() => {
    const inner = innerRef.current;
    if (!showMenu || inner === null || inner.contains(document.activeElement)) {
      return;
    }
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [showMenu]);

  useEffect(() => {
    const inner = innerRef.current;
    if (!showMenu || inner === null || inner.contains(document.activeElement)) {
      return;
    }
    inner.focus({ preventScroll: true });
  }, [showMenu, menuKey]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    return () => {
      const returnFocus = returnFocusRef.current;
      returnFocusRef.current = null;
      if (returnFocus?.isConnected) {
        returnFocus.focus({ preventScroll: true });
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!showMenu) {
      return;
    }
    const keepFocusInside = (event: KeyboardEvent) => {
      const inner = innerRef.current;
      if (event.key !== "Tab" || inner === null) {
        return;
      }
      const items = Array.from(inner.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        event.preventDefault();
        inner.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!inner.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || active === inner)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keepFocusInside, true);
    return () => document.removeEventListener("keydown", keepFocusInside, true);
  }, [showMenu]);

  useEffect(() => {
    if (closeRequested && showMenu) {
      handleCloseMenuRef.current();
    }
  }, [closeRequested, showMenu]);

  useEffect(() => {
    if (!switchingToMenu) return;
    setIsClosing(true);
    setUseFastClose(true);
  }, [switchingToMenu]);

  useEffect(() => {
    if (!showMenu) setUseFastOpen(false);
  }, [showMenu]);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const isSwitchClosing = !!(switchingToMenu && useFastClose && isClosing);
    if (!isSwitchClosing) return;

    const handleAnimationEnd = (e: AnimationEvent) => {
      if (e.target !== el) return;
      onSwitchComplete?.(switchingToMenu!);
      setUseFastOpen(true);
      setIsClosing(false);
      setUseFastClose(false);
    };
    el.addEventListener("animationend", handleAnimationEnd);
    return () => el.removeEventListener("animationend", handleAnimationEnd);
  }, [switchingToMenu, useFastClose, isClosing, onSwitchComplete]);

  const innerClosingClass = useFastClose ? style.drawerMenuInnerClosingFast : style.drawerMenuInnerClosing;
  const innerClass = `${style.drawerMenuInner} ${isClosing ? innerClosingClass : useFastOpen ? style.drawerMenuInnerFastOpen : ""}`;

  const isOverHeaderLayout = layoutVariant === "overHeader";

  return (
    isVisible && (
      <div
        ref={menuRef}
        className={`${style.drawerMenuContainer} ${isOverHeaderLayout ? style.drawerMenuOverHeader : ""} ${isMobile ? mobileMenuClassname : ""} ${preserveLowerNav ? style.preserveLowerNav : ""}`}
        id={id}
      >
        <div
          ref={innerRef}
          key={menuKey ?? "default"}
          className={innerClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headerText ? titleId : undefined}
          tabIndex={-1}
        >
          <div className={`${style.menuHeaderContainer} ${headerLine ? style.headerLine : ""}`}>
            <div className={style.menuHeaderLeftAlign}>
              {showBackArrow && (
                <button type="button" className={style.headerMenuBackContainer} onClick={onBack} aria-label="Back">
                  <CaretLeftIcon className={`${style.drawerMenuBack} `} aria-hidden />
                </button>
              )}
              {headerText && (
                <div id={titleId} className={`${style.drawerMenuHeader} ${showBackArrow ? style.noLeftPad : ""}`}>
                  {headerText}
                </div>
              )}
            </div>
            <button
              type="button"
              className={style.drawerMenuCloseContainer}
              onClick={handleCloseMenu}
              aria-label="Close"
            >
              <CloseIcon width="16px" height="16px" className={style.drawerMenuClose} aria-hidden />
            </button>
          </div>
          {children}
        </div>
      </div>
    )
  );
}

export type DrawerMenuSwitchState = {
  switchingToMenu: OverlayMenuType | null;
  onSwitchComplete: (menu: OverlayMenuType) => void;
  menuKey: string;
};

export type DrawerMenuCloseOptions = {
  closeRequested?: boolean;
  /** Selectors for menu triggers to ignore (e.g. other menus); active menu is excluded so clicking it closes. */
  menuTriggerIgnoreSelectors?: string[];
};

export type DrawerMenuProps = {
  showMenu: boolean;
  showBackArrow?: boolean;
  headerText?: string;
  children: React.ReactNode;
  id: string;
  mobileMenuClass: string;
  onClose: () => void;
  onBack: () => void;
  preserveLowerNav?: boolean;
  /** State for fast menu switch (close then open another); when omitted, switch behavior is disabled. */
  switchState?: DrawerMenuSwitchState | null;
  /** Options for close behavior (request close with animation, which triggers to ignore for outside-click). */
  closeOptions?: DrawerMenuCloseOptions;
  /**
   * `belowHeader`: drawer is anchored under sticky header (account/support/deals).
   * `overHeader`: full height side panel on desktop.
   * Note: on mobile it will always be the same.
   */
  layoutVariant?: OverlayDrawerLayoutVariant;
  /** A 1px line under the over-header layout's header instead of its shadow. */
  headerLine?: boolean;
};
